import { useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { EditLegendContainer } from '../../Legend';
import { LegendItem } from '../../../components/Legend';
import styles from './HarvestAndPlantingLegend.css';
import {
  reqLegend as requestLegendAction,
  toggleEditLegend as toggleEditLegendAction
} from '../../../actions/legendActions';
import { trackEvent } from '../../../actions/analyticsActions';
import {
  getCropNominalWeight,
  processIntervals,
  isLocalizeIntervalsReturnType
} from '../../../utils/processUnits';
import { usernameSelector, userSelector } from '../../../selectors/userSelectors';
import { getArdDataFeatureSelector } from '../../../selectors/featureSelectors';
import { PLANTING } from '../../../constants/mapLayerIds';
import { DMB_AG_VIZ_LEGEND_EDITION } from '../../../constants/featureFlags';
import { Interval, EventDates, User } from '../../../types';
import { PartialWithRequired } from '../../../../types/utils';
import { getCropUnits } from './cropUnitsUtil';
import { logDmbError } from '../../../utils/error'
import { getCurrentLayerId, getCurrentSeason } from '../../../selectors';

export interface HarvestAndPlantingLegendProps {
  agVizEnabled?: boolean;
  legendEditionEnabled?: boolean;
  crop: string;
  cropCode?: string;
  currentUserHasFetched: boolean;
  dates: EventDates;
  fieldUuid: string,
  index: number;
  intervals?: Interval[];
  isFetching?: boolean;
  isLayerAvailable?: boolean;
  layer: string;
  locale: string;
  reqLegend?: typeof requestLegendAction;
  season: string;
  testId: string;
  toggleEditLegend?: typeof toggleEditLegendAction;
  trackAnalyticsEvent: (noun: string, verb: string, data: object) => unknown;
  units?: object;
  unitsLocaleGroup?: string;
  username: string;
  user: PartialWithRequired<User, 'uuid'>;
}

export const HarvestAndPlantingLegend = ({
  agVizEnabled,
  legendEditionEnabled,
  crop,
  cropCode,
  currentUserHasFetched,
  dates,
  fieldUuid,
  index,
  isFetching,
  isLayerAvailable = true,
  layer,
  locale,
  reqLegend: reqLegendCallback,
  season,
  testId,
  toggleEditLegend,
  trackAnalyticsEvent,
  units,
  unitsLocaleGroup,
  username,
  user,
  intervals = []
}: HarvestAndPlantingLegendProps) => {
  useEffect(() => {
    const useLegacyLegends =
      isLayerAvailable && !intervals.length && reqLegendCallback && !agVizEnabled

    if (useLegacyLegends) {
      reqLegendCallback(username, fieldUuid, dates, crop, layer, season, undefined, user.uuid);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const prevPropsRef = useRef({ layer, season });
  useEffect(() => {
    const { layer: prevLayer, season: prevSeason } = prevPropsRef.current;

    // Don't request legend if Ag-Viz feature flag is enabled - Ag-Viz saga will provide it
    if (
      (prevLayer !== layer || prevSeason !== season) &&
      isLayerAvailable &&
      !intervals.length
      && reqLegendCallback
      && !agVizEnabled
    ) {
      reqLegendCallback(username, fieldUuid, dates, crop, layer, season, undefined, user.uuid);
    }

    prevPropsRef.current = { layer, season };
  }, [layer, season, intervals, username, fieldUuid, dates, crop, user?.uuid, reqLegendCallback, agVizEnabled, isLayerAvailable]);

  const toggleEditLegendCallback = () => {
    if (toggleEditLegend) {
      toggleEditLegend(index);
    }
    trackAnalyticsEvent('edit_legend', 'clicked', {
      numberOfSteps: intervals.length,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      min: intervals[0] && intervals[0].value,
      max:
        intervals[intervals.length - 1] &&
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        intervals[intervals.length - 1].value,
    });
  };

  if (!isLayerAvailable) {
    return null;
  }

  if (!currentUserHasFetched || !intervals.length) {
    return <LegendItem isFetching/>;
  }

  let finalIntervals;
  try {
    finalIntervals = processIntervals({intervals, units, locale, cropCode, agVizEnabled});
  } catch (error) {
    logDmbError(error, {
      processIntervals: 'harvestAndPlantingLegend',
      intervals,
      units,
      locale,
      cropCode,
      agVizEnabled
    });

    return null;
  }
  
  const unitHasKey = isLocalizeIntervalsReturnType(finalIntervals);

  // For Ag-Viz (ARD) legends the values are localized from the legend's own native
  // unit (e.g. kg/ha for yield), which can differ from the static layer default
  // (e.g. tonne/hectare). Forward that native unit so the edit form converts the
  // user's display values back to the exact unit the mutation expects.
  //
  // Gated behind the same flags as the ARD save path (agVizEnabled &&
  // legendEditionEnabled) so the legacy legend-edit flow never sees a baseUnitKey
  // and keeps its previous getLayerUnits-based conversion untouched.
  const baseUnitKey = agVizEnabled && legendEditionEnabled
    ? (units as { key?: string })?.key
    : undefined;

  const forwardUnits = unitHasKey ? {
      key: finalIntervals.key,
      singularKey: finalIntervals.singularKey,
      cropNominalWeight: finalIntervals.cropNominalWeight,
      baseUnitKey
    } : {
      key: undefined,
      singularKey: undefined,
      cropNominalWeight: undefined,
      intervals: finalIntervals.intervals
    }

  return (
    <div
      className={styles.legendContainer}
      data-test-id={`${testId}-harvestPlanting-${layer}`}
    >
      <EditLegendContainer
        crop={crop}
        dates={dates}
        index={index}
        intervals={finalIntervals.intervals}
        units={forwardUnits}
        unitsLocaleGroup={unitsLocaleGroup}
        year={dates.year}
        processedIntervals={finalIntervals}
      />
      <LegendItem
        intervals={finalIntervals.intervals}
        isEditable={(!agVizEnabled || Boolean(legendEditionEnabled)) && (unitHasKey || layer === PLANTING.CEC)}
        isFetching={isFetching ?? false}
        toggleEditLegend={toggleEditLegendCallback}
        units={forwardUnits?.key}
        agVizEnabled={agVizEnabled}
      />
    </div>
  );
}

export function mapStateToProps(state, props) {
  const activeSeason = getCurrentSeason(state, props);
  const activeMapLayer = getCurrentLayerId(state, props);
  const currentLayer =
    state.layers.list?.[activeSeason]?.[activeMapLayer];
  const { legend, localeGroup } = currentLayer || {};

  const agVizEnabled = getArdDataFeatureSelector(state);
  // When Ag-Viz is on, legend editing is re-enabled only behind this flag.
  const legendEditionEnabled =
    state.feature?.userConfigFeatures?.[DMB_AG_VIZ_LEGEND_EDITION]?.value || false;


  const { crop = '', cropCustomName = '' } = state.seasons.list?.[activeSeason] ?? {};
  const cropCode = state.crops[crop] && state.crops[crop].code;
  const cropNominalWeight = getCropNominalWeight(crop, state.crops);
  const units = getCropUnits({
    cropCode,
    activeMapLayer: { id: activeMapLayer },
    localeGroup,
    agVizEnabled,
    agVizLayerUnits: currentLayer?.agVizUnits
  });

  return {
    agVizEnabled,
    legendEditionEnabled,
    crop: cropCustomName,
    cropCode,
    currentUserHasFetched: state.currentUser?.hasFetched ?? false,
    fieldUuid: state.field.uuid?.toUpperCase() ?? '',
    intervals: legend,
    isFetching: state.legend.isFetching,
    isLayerAvailable: Boolean(currentLayer),
    layer: activeMapLayer,
    locale: state.app.locale,
    season: activeSeason,
    username: usernameSelector(state),
    units: { ...units, cropNominalWeight },
    unitsLocaleGroup: localeGroup,
    user: userSelector(state)
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      reqLegend: requestLegendAction,
      toggleEditLegend: toggleEditLegendAction,
      trackAnalyticsEvent: trackEvent,
    },
    dispatch
  );
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(HarvestAndPlantingLegend);
